import { useNotifications, useMarkNotificationRead, useMarkAllRead } from '@/hooks/useNotifications';
import { Bell, CheckCheck, Loader2, Receipt, Users, Handshake, AlertCircle } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useTranslation } from 'react-i18next';

const NOTIFICATION_ICONS: Record<string, React.ElementType> = {
  expense_added: Receipt,
  expense_updated: Receipt,
  expense_deleted: Receipt,
  member_joined: Users,
  member_left: Users,
  settlement_made: Handshake,
  settlement_received: Handshake,
  group_updated: Users,
};

export function NotificationsPage() {
  const { t } = useTranslation();
  const { data: notifications, isLoading } = useNotifications();
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllRead();

  const unreadCount = notifications?.filter(n => !n.read).length ?? 0;

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-brand-600" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-text-primary">{t('notifications:title')}</h1>
        {unreadCount > 0 && (
          <button
            onClick={() => markAllRead.mutate()}
            disabled={markAllRead.isPending}
            className="flex items-center gap-1.5 rounded-lg border border-border-secondary px-3 py-1.5 text-sm text-text-secondary hover:bg-surface-hover disabled:opacity-50"
          >
            <CheckCheck className="h-4 w-4" />
            {t('notifications:action.markAllRead')}
          </button>
        )}
      </div>

      {notifications && notifications.length > 0 ? (
        <div className="space-y-1">
          {notifications.map(notification => {
            const Icon = NOTIFICATION_ICONS[notification.type] ?? AlertCircle;
            return (
              <button
                key={notification.id}
                onClick={() => {
                  if (!notification.read) {
                    markRead.mutate(notification.id);
                  }
                }}
                className={`flex w-full items-start gap-3 rounded-lg p-4 text-left transition-colors ${
                  notification.read
                    ? 'bg-surface-primary hover:bg-surface-hover'
                    : 'bg-brand-50 dark:bg-brand-900/20 hover:bg-brand-100 dark:hover:bg-brand-900/40'
                }`}
              >
                <div className={`mt-0.5 flex h-8 w-8 items-center justify-center rounded-full shrink-0 ${
                  notification.read ? 'bg-surface-tertiary' : 'bg-brand-100 dark:bg-brand-900/40'
                }`}>
                  <Icon className={`h-4 w-4 ${notification.read ? 'text-text-quaternary' : 'text-brand-600'}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm ${notification.read ? 'text-text-secondary' : 'font-medium text-text-primary'}`}>
                    {notification.title}
                  </p>
                  {notification.message && (
                    <p className="mt-0.5 text-xs text-text-tertiary truncate">{notification.message}</p>
                  )}
                  <p className="mt-1 text-xs text-text-quaternary">
                    {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                  </p>
                </div>
                {!notification.read && (
                  <div className="mt-2 h-2 w-2 rounded-full bg-brand-600 shrink-0" />
                )}
              </button>
            );
          })}
        </div>
      ) : (
        <div className="flex flex-col items-center rounded-xl bg-surface-primary p-12 shadow-sm ring-1 ring-border-primary text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-surface-tertiary">
            <Bell className="h-6 w-6 text-text-quaternary" />
          </div>
          <h3 className="mt-4 text-sm font-medium text-text-primary">{t('web:notificationsPage.allCaughtUp')}</h3>
          <p className="mt-1 text-sm text-text-tertiary">{t('web:notificationsPage.youHaveNoNotifications')}</p>
        </div>
      )}
    </div>
  );
}
